import { Box, Divider, Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useAddUserZoneAssociationUsersUserIdZonesZoneIdPostMutation,
  useRemoveUserZoneAssociationUsersUserIdZonesZoneIdDeleteMutation,
  useUpdateUserUsersIdPatchMutation,
} from "api";
import { RoleInput, TextInput, ZoneInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { IUser } from "interfaces/user.interface";
import { IZone } from "interfaces/zone.interface";

import { UserFormSchema, userFormSchema } from "./userFormSchema";

import { FieldColumns } from "../../components/FieldColumns";
import { FormActions } from "../../components/FormActions";
import { PageHeader } from "../../components/PageHeader";
import { DeleteUserDialog } from "../dialogs/DeleteUserDialog";

type CreateUserProps = {
  zones: IZone[];
  user: IUser;
};

export function EditUserForm({ zones, user }: CreateUserProps) {
  const { t } = useTranslation();

  const [updateUser, { isLoading: isUpdating }] =
    useUpdateUserUsersIdPatchMutation();
  const [addZone, { isLoading: isAddingZone }] =
    useAddUserZoneAssociationUsersUserIdZonesZoneIdPostMutation();

  const [removeZone, { isLoading: isRemovingZone }] =
    useRemoveUserZoneAssociationUsersUserIdZonesZoneIdDeleteMutation();

  const { control, handleSubmit, reset } = useForm<UserFormSchema>({
    resolver: zodResolver(userFormSchema),
    defaultValues: userFormSchema.parse({
      ...user,
      zones: user.zones?.data?.map((zone) => zone.id) ?? [],
    }),
  });

  const onSubmit = async (data: UserFormSchema) => {
    const currentZoneIds = user.zones?.data?.map((zone) => zone.id) ?? [];

    const addZones = data.zones.filter(
      (zoneId) => !currentZoneIds.includes(zoneId),
    );

    const removeZones = currentZoneIds.filter(
      (zoneId) => !data.zones.includes(zoneId),
    );

    // Change zones first so that the updated user has the zones
    await Promise.all([
      ...addZones.map((zoneId) => addZone({ userId: user.id, zoneId })),
      ...removeZones.map((zoneId) => removeZone({ userId: user.id, zoneId })),
    ]);

    const { data: userData } = await updateUser({
      id: user.id,
      userUpdate: {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        profession: data.profession ?? "",
        roles: data.roles,
        preferences: data.preferences,
        metadata: data.metadata,
      },
    });

    const updatedUser = userData?.data;

    if (updatedUser) {
      reset(
        userFormSchema.parse({
          ...updatedUser,
          zones: data.zones,
        }),
      );
    }
  };

  return (
    <Stack gap={2} component={"form"} onSubmit={handleSubmit(onSubmit)}>
      <PageHeader title={user.first_name + " " + user.last_name} />
      <SectionCard title={t("employee_information")}>
        <FieldColumns>
          <TextInput
            label={t("First Name")}
            control={control}
            fieldName={"first_name"}
            placeholder={t("user_first_name_placeholder")}
            required
          />

          <TextInput
            label={t("Last Name")}
            control={control}
            fieldName={"last_name"}
            placeholder={t("user_last_name_placeholder")}
            required
          />

          <TextInput
            label={t("Email")}
            type={"email"}
            control={control}
            fieldName={"email"}
            placeholder={t("user_email_placeholder")}
            required
          />

          <TextInput
            label={t("Job Title")}
            control={control}
            fieldName={"profession"}
            placeholder={t("user_title_placeholder")}
          />
        </FieldColumns>
      </SectionCard>
      <SectionCard title={t("Zones and Roles")}>
        <Box
          display={"flex"}
          gap={2}
          width={"100%"}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          <Box display={"flex"} flexDirection={"column"} flex={1} gap={2}>
            <ZoneInput
              label={t("Zones")}
              control={control}
              fieldName={"zones"}
              zones={zones}
            />
          </Box>
          <Divider sx={{ display: { xs: "initial", lg: "none" } }} />
          <Box display={"flex"} flexDirection={"column"} flex={1} gap={2}>
            <RoleInput
              label={t("Role(s)")}
              control={control}
              fieldName={"roles"}
            />
          </Box>
        </Box>
      </SectionCard>
      <FormActions
        submitText={t("Update")}
        backText={t("Cancel")}
        backHref={"../users"}
        isLoading={isUpdating || isAddingZone || isRemovingZone}
      >
        <DeleteUserDialog userId={user.id} />
      </FormActions>
    </Stack>
  );
}
